'use client';

import { NftCollectionsAPI } from '@/lib/services/catalog/nftcollection.service';
import { SelectedFiltersType } from '@/lib/services/catalog/catalog-types';
import { ArtworksAPI } from '@/lib/services/catalog/artwork.service';
import { PaginatedResponse, CatalogItem } from '@/lib/services/catalog/catalog-types';
import { PhysicalsAPI } from '@/lib/services/catalog/physicals.service';
import { InstallationsAPI } from '@/lib/services/catalog/installations.service';
import { ImmersiveAPI } from '@/lib/services/catalog/immersives.service';
import { ExhibitionsAPI } from '@/lib/services/catalog/exhibitions.service';
import { CollaborationsAPI } from '@/lib/services/catalog/collaborations.service';
import { WritingsAPI } from '@/lib/services/catalog/writings.service';

export interface CatalogAPIProvidersProps {
  collectionKey?: string; // Geriye dönük uyumluluk için
  collectionKeys?: readonly string[];
  page: number;
  limit: number;
  query: string;
  sort: string;
  selectedFilters?: SelectedFiltersType;
  collectionid?: number;
}

// Yardımcı fonksiyon: Belirli bir koleksiyon için API çağrısı yapar
const fetchCollectionData = async (
  key: string,
  params: {
    page: number;
    limit: number;
    query: string;
    sort: string;
    selectedFilters?: SelectedFiltersType;
    collectionid?: number;
  }
): Promise<PaginatedResponse<CatalogItem> | null> => {
  const { page, limit, query, sort, selectedFilters, collectionid } = params;

  switch (key) {
    case 'nftCollections': {
      const nftParams: Record<string, unknown> = {
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        medium: selectedFilters?.medium,
        platform: selectedFilters?.platform,
        chains: selectedFilters?.chains,
        type: selectedFilters?.type,
      };
      // When viewing a specific collection, fetch its child collections (matching server behavior)
      if (collectionid && collectionid > 0) {
        nftParams.getChildCollections = true;
        nftParams.parentid = collectionid;
      }
      const result = await NftCollectionsAPI.findNftCollections(nftParams);
      return result.data || null;
    }
    case 'artworks':
      if (collectionid > 0) {
        const subNftCollections = await NftCollectionsAPI.findNftCollections({
          limit: 1000,
          getChildCollections: true,
          parentid: collectionid,
          select: {
            collection: true,
          },
        });
        if (subNftCollections.data && subNftCollections.data.docs.length > 0) {
          const result = await ArtworksAPI.findArtworks({
            page,
            limit,
            query: query || undefined,
            sort: sort,
            years: selectedFilters?.years,
            medium: selectedFilters?.medium,
            platform: selectedFilters?.platform,
            chains: selectedFilters?.chains,
            type: selectedFilters?.type,
            where: {
              collection: {
                in: [collectionid, ...subNftCollections.data.docs.map(collection => collection.id)],
              },
            },
            collectionid,
          });
          return result.data || null;
        } else {
          const result = await ArtworksAPI.findArtworks({
            page,
            limit,
            query: query || undefined,
            sort: sort,
            years: selectedFilters?.years,
            medium: selectedFilters?.medium,
            platform: selectedFilters?.platform,
            chains: selectedFilters?.chains,
            type: selectedFilters?.type,
            collectionid,
          });
          return result.data || null;
        }
      } else {
        const result = await ArtworksAPI.findArtworks({
          page,
          limit,
          query: query || undefined,
          sort: sort,
          years: selectedFilters?.years,
          medium: selectedFilters?.medium,
          platform: selectedFilters?.platform,
          chains: selectedFilters?.chains,
          type: selectedFilters?.type,
        });
        return result.data || null;
      }
    case 'oneofone': {
      const result = await ArtworksAPI.findArtworks({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        medium: selectedFilters?.medium,
        platform: selectedFilters?.platform,
        chains: selectedFilters?.chains,
        type: selectedFilters?.type,
        oneofone: true,
      });
      return result.data || null;
    }
    case 'installations': {
      const result = await InstallationsAPI.findInstallations({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        type: selectedFilters?.type,
      });
      return result.data || null;
    }
    case 'physicals': {
      const result = await PhysicalsAPI.findPhysicals({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        type: selectedFilters?.type,
      });
      return result.data || null;
    }
    case 'immersive': {
      const result = await ImmersiveAPI.findImmersives({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        type: selectedFilters?.type,
      });
      return result.data || null;
    }
    case 'exhibitions': {
      const result = await ExhibitionsAPI.findExhibitions({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        type: selectedFilters?.type,
      });
      return result.data || null;
    }
    case 'collaborations': {
      const result = await CollaborationsAPI.findCollaborations({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        type: selectedFilters?.type,
      });
      return result.data || null;
    }
    case 'writings': {
      const result = await WritingsAPI.findWritings({
        page,
        limit,
        query: query || undefined,
        sort: sort,
        years: selectedFilters?.years,
        category: selectedFilters?.category,
      });
      return result.data || null;
    }
    default:
      return null;
  }
};

// Koleksiyon verilerini izleme için bir cache
const fetchedCollections = new Set<string>();

export const CatalogAPIProviders = async ({
  collectionKey,
  collectionKeys,
  collectionid,
  page,
  limit,
  query,
  sort,
  selectedFilters,
}: CatalogAPIProvidersProps) => {
  // Geriye dönük uyumluluk için
  const keys = collectionKeys || (collectionKey ? [collectionKey] : []);

  // Eğer koleksiyon anahtarı yoksa, boş sonuç döndür
  if (keys.length === 0) {
    return null;
  }

  // Reset tracking on first page to avoid stale state across SPA navigations
  if (page === 1) {
    fetchedCollections.clear();
  }

  // Tek bir koleksiyon anahtarı varsa, doğrudan o koleksiyonun API'sini çağır
  if (keys.length === 1) {
    return await fetchCollectionData(keys[0], { page, limit, query, sort, selectedFilters, collectionid });
  }

  // Birden fazla koleksiyon anahtarı varsa, tüm koleksiyonlar için API çağrılarını yap ve sonuçları birleştir
  const results = await Promise.all(
    keys.map(async key => {
      // Eğer ilk sayfada değilsek ve bu koleksiyon için tüm veriler zaten çekildiyse, boş sonuç döndür
      if (page > 1 && fetchedCollections.has(key)) {
        // logger.warn(`${key} koleksiyonu için tüm veriler zaten çekildi, istek yapılmıyor.`);
        return {
          docs: [],
          totalDocs: 0,
          limit,
          totalPages: 0,
          page,
          pagingCounter: 0,
          hasPrevPage: false,
          hasNextPage: false,
          prevPage: null,
          nextPage: null,
        };
      }

      const result = await fetchCollectionData(key, { page, limit, query, sort, selectedFilters, collectionid });

      // Check and log IDs for duplicates
      if (result && result.docs) {
        const idCounts: Record<string | number, number> = {};
        result.docs.forEach(item => {
          if (idCounts[item.id]) {
            idCounts[item.id]++;
            // logger.error(`DUPLICATE ID FOUND in ${key}: ${item.id} (${idCounts[item.id]} occurrences)`);
          } else {
            idCounts[item.id] = 1;
          }
        });
      }

      // Eğer sonuç varsa ve sonraki sayfa yoksa, bu koleksiyonun tüm verilerini çektiğimizi işaretle
      if (result && !result.hasNextPage) {
        fetchedCollections.add(key);
        // logger.warn(`${key} koleksiyonu için tüm veriler çekildi.`);
      }

      return result;
    })
  );

  // Null olmayan sonuçları filtrele
  const validResults = results.filter(result => result !== null);

  // Eğer hiçbir geçerli sonuç yoksa, null döndür
  if (validResults.length === 0) {
    return null;
  }

  // Tüm sonuçları birleştir
  const combinedResult = {
    docs: validResults.flatMap((result, resultIndex) => {
      // Her koleksiyon için benzersiz bir önek oluştur
      const collectionKey = keys[resultIndex];

      // Docs dizisini dönüştür ve her öğeye koleksiyon anahtarını ekle
      return (result?.docs || []).map((item, itemIndex) => {
        // Benzersiz bir ID oluştur: koleksiyon_id_index
        const uniqueId = `${collectionKey}_${item.id}_${itemIndex}`;

        return {
          ...item,
          // Orijinal ID'yi korumak için _originalId ekle ve id'yi benzersiz hale getir
          _originalId: item.id,
          id: uniqueId,
        };
      });
    }),
    totalDocs: validResults.reduce((sum, result) => sum + (result?.totalDocs || 0), 0),
    limit,
    totalPages: Math.max(...validResults.map(result => result?.totalPages || 0)),
    page,
    pagingCounter: validResults[0]?.pagingCounter || 0,
    hasPrevPage: validResults.some(result => result?.hasPrevPage),
    // Sadece tüm koleksiyonlar için hasNextPage false ise, hasNextPage false olmalı
    hasNextPage: validResults.some(result => result?.hasNextPage),
    prevPage: validResults[0]?.prevPage,
    nextPage: validResults[0]?.nextPage,
  };

  return combinedResult;
};
